import { useState, useEffect, useCallback } from "react";
import { useUser, useAuth } from "@clerk/clerk-react";
import { geteilteListe, listeVerwerfen, objektAusListe } from "../lib/portfolioCache.js";

export type AnalyzerType = "etw" | "mfh" | "efh" | "gewerbe" | "mixeduse";

export interface PortfolioObject {
  id: string;
  user_id: string;
  analyzer_type: AnalyzerType;
  name: string;
  adresse?: string;
  plz?: string;
  kaufpreis?: number;
  status?: string;
  data: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

export interface PortfolioSummary {
  objectCount: number;
  byType: Record<AnalyzerType, number>;
}

// SICHERHEITSFIX (2026-09-17): sprach vorher direkt mit dem anon-Key gegen
// Supabase -- ohne Supabase-Auth-Session (dieses Projekt nutzt Clerk) gab es
// keine Moeglichkeit, das per RLS auf den jeweiligen Nutzer einzuschraenken.
// Jetzt ausschliesslich ueber api/portfolio.ts (Clerk-Bearer-Token,
// serverseitig mit clerkClient.verifyToken geprueft, Service-Role-Key erst
// danach). Siehe api/portfolio.ts fuer die volle Begruendung.
//
// Bugfix 16.10.2026 (Dominiks Live-Test: Seitenleiste "Meine Objekte" zeigte nach dem
// Speichern eines Deals weiter den alten Score): usePortfolio() haelt seine `objects`-Liste
// PRO Aufrufstelle in eigenem useState -- AppShell (Seitenleiste) und z. B. DealDetail/
// SaveToPortfolioButton rufen den Hook je einzeln auf und bekamen dadurch bisher nie
// etwas von den Mutationen der anderen Stelle mit, ohne vollen Reload. Schlanker Fix statt
// eines App-weiten Context/Providers (15 Aufrufstellen, siehe grep): ein modul-weiter
// Listener-Set -- jede Hook-Instanz meldet ihre eigene load()-Funktion an, jede mutierende
// Aktion (save/update/remove/updateStatus/rename) benachrichtigt nach Erfolg ALLE
// angemeldeten Instanzen (inkl. der eigenen, ein doppeltes load() dort ist unschaedlich).
const portfolioListeners = new Set<() => void>();
// Ladezeit 18.10.2026: alle Instanzen teilen sich eine Liste (siehe lib/portfolioCache.ts);
// nach einer Änderung wird sie einmal verworfen, die Listener laden dann gemeinsam neu.
function notifyPortfolioChanged() {
  listeVerwerfen();
  portfolioListeners.forEach((listener) => listener());
}

export function usePortfolio() {
  const { user } = useUser();
  const { getToken } = useAuth();
  const [objects, setObjects] = useState<PortfolioObject[]>([]);
  const [loading, setLoading] = useState(false);
  // true, sobald die Liste einmal fertig geladen (oder fehlgeschlagen) ist – für Weiterleitungen,
  // die zwischen „noch nicht geladen“ und „leer“ unterscheiden müssen (StartUmleitung).
  const [geladen, setGeladen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const userId = user?.id;

  const call = useCallback(async (action: string, payload: Record<string, unknown> = {}) => {
    const token = await getToken();
    const res = await fetch("/api/portfolio", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ action, ...payload }),
    });
    const json = await res.json();
    if (!res.ok) throw new Error(json.error || "Anfrage fehlgeschlagen");
    return json;
  }, [getToken]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await geteilteListe<PortfolioObject>(userId, async () => (await call("list")).objects ?? []);
      setObjects(data);
    } catch (e: any) {
      setError(e.message ?? "Fehler beim Laden");
    } finally {
      setLoading(false);
      setGeladen(true);
    }
  }, [userId, call]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    portfolioListeners.add(load);
    return () => { portfolioListeners.delete(load); };
  }, [load]);

  /** Wie save(), liefert aber den Grund bei Misserfolg („OBJEKT_LIMIT“ = FREE-Grenze erreicht). */
  const saveMitGrund = useCallback(async (params: {
    analyzer_type: AnalyzerType;
    name: string;
    adresse?: string;
    plz?: string;
    kaufpreis?: number;
    data: Record<string, unknown>;
  }): Promise<{ ok: true } | { ok: false; grund: string }> => {
    if (!userId) return { ok: false, grund: "UNAUTHENTICATED" };
    setError(null);
    try {
      await call("save", params);
      listeVerwerfen();
      await load();
      portfolioListeners.forEach((listener) => listener());
      return { ok: true };
    } catch (e: any) {
      setError(e.message ?? "Fehler beim Speichern");
      return { ok: false, grund: String(e?.message ?? "SAVE_FAILED") };
    }
  }, [userId, call, load]);

  const save = useCallback(async (params: {
    analyzer_type: AnalyzerType;
    name: string;
    adresse?: string;
    plz?: string;
    kaufpreis?: number;
    data: Record<string, unknown>;
  }): Promise<boolean> => {
    if (!userId) return false;
    setError(null);
    try {
      await call("save", params);
      // Erst die gemeinsame Liste verwerfen, dann neu laden – sonst lieferte load() die
      // zwischengespeicherte (alte) Liste. Danach die anderen Instanzen ohne erneutes
      // Verwerfen informieren: sie bekommen die gerade geladene Liste.
      listeVerwerfen();
      await load();
      portfolioListeners.forEach((listener) => listener());
      return true;
    } catch (e: any) {
      setError(e.message ?? "Fehler beim Speichern");
      return false;
    }
  }, [userId, call, load]);

  /** Phase-4 Runde 2a: "In Deal uebernehmen" -- aktualisiert denselben Deal (data/
   *  name/adresse/plz/kaufpreis), statt per save() einen neuen anzulegen. id/status/
   *  analyzer_type sind hier nie Teil des Patches (siehe api/portfolio.ts, action
   *  "update"). */
  const update = useCallback(async (id: string, params: {
    name: string;
    adresse?: string;
    plz?: string;
    kaufpreis?: number;
    data: Record<string, unknown>;
  }): Promise<boolean> => {
    if (!userId) return false;
    setError(null);
    try {
      await call("update", { id, ...params });
      // Erst die gemeinsame Liste verwerfen, dann neu laden – sonst lieferte load() die
      // zwischengespeicherte (alte) Liste. Danach die anderen Instanzen ohne erneutes
      // Verwerfen informieren: sie bekommen die gerade geladene Liste.
      listeVerwerfen();
      await load();
      portfolioListeners.forEach((listener) => listener());
      return true;
    } catch (e: any) {
      setError(e.message ?? "Fehler beim Übernehmen");
      return false;
    }
  }, [userId, call, load]);

  /** Einzelnes Objekt laden (fuer "Wiederoeffnen" per ?dealId=). Serverseitig
   *  auf den eigenen Nutzer geprueft (api/portfolio.ts, action "get"). */
  const get = useCallback(async (id: string): Promise<PortfolioObject | null> => {
    if (!userId) return null;
    const ausListe = objektAusListe<PortfolioObject>(userId, id);
    if (ausListe) return ausListe;
    try {
      const { object } = await call("get", { id });
      return object ?? null;
    } catch {
      return null;
    }
  }, [userId, call]);

  const remove = useCallback(async (id: string): Promise<boolean> => {
    if (!userId) return false;
    setError(null);
    try {
      await call("delete", { id });
      setObjects(prev => prev.filter(o => o.id !== id));
      notifyPortfolioChanged();
      return true;
    } catch (e: any) {
      setError(e.message ?? "Fehler beim Löschen");
      return false;
    }
  }, [userId, call]);

  const updateStatus = useCallback(async (id: string, status: string): Promise<boolean> => {
    if (!userId) return false;
    try {
      await call("updateStatus", { id, status });
      setObjects(prev => prev.map(o => o.id === id ? { ...o, status } : o));
      notifyPortfolioChanged();
      return true;
    } catch {
      return false;
    }
  }, [userId, call]);

  /** Nachtrag Phase-4 Runde 2a ("Meine Deals" ins neue Design, Punkt 2): reines
   *  Namens-Update -- schickt NUR {id, name} (siehe api/portfolio.ts, action "update"),
   *  adresse/plz/kaufpreis/data bleiben unberuehrt. name darf "" sein ("zurueck auf den
   *  Standardnamen", buildDealHeading()/dealNaming.ts behandelt das bereits korrekt). */
  const rename = useCallback(async (id: string, name: string): Promise<boolean> => {
    if (!userId) return false;
    setError(null);
    try {
      await call("update", { id, name });
      setObjects(prev => prev.map(o => o.id === id ? { ...o, name } : o));
      notifyPortfolioChanged();
      return true;
    } catch (e: any) {
      setError(e.message ?? "Fehler beim Umbenennen");
      return false;
    }
  }, [userId, call]);

  const summary: PortfolioSummary = {
    objectCount: objects.length,
    byType: {
      etw: objects.filter(o => o.analyzer_type === "etw").length,
      mfh: objects.filter(o => o.analyzer_type === "mfh").length,
      efh: objects.filter(o => o.analyzer_type === "efh").length,
      gewerbe: objects.filter(o => o.analyzer_type === "gewerbe").length,
      mixeduse: objects.filter(o => o.analyzer_type === "mixeduse").length,
    }
  };

  return { objects, loading, geladen, error, save, saveMitGrund, update, get, remove, reload: load, summary, updateStatus, rename };
}
